import { useCallback, useContext } from 'react';
import { toast } from 'sonner';
import { Context, ErrorEntity, Error as ErrorI } from '../../components/root/context';

// Interface for options that can be passed to the apiRequest function
interface ApiRequestOptions<T> {
  // Optional toast configuration
  toast?: { toastText?: string };
  // Optional body for the request
  body?: any;
  test?: boolean;
  mockError?: ErrorI;
  mockResponse?: T;
}

// Interface for the response returned by the apiRequest function
interface ApiResponse<T> {
  data: T | undefined;
  error: ErrorI | unknown | undefined;
  status: number;
}

type ApiFileRequestOptions = {
  formdataName: string;
  customFields?: { [key: string]: string };
  toast?: {
    toastText?: string;
  };
};

// Custom hook to create an API request function
const useApiRequest = () => {
  const { setError, accessToken } = useContext(Context);
  // useCallback to memoize the apiRequest function
  const apiRequest = useCallback(
    async <T>(
      route: string, // API route
      method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', // HTTP method
      options?: ApiRequestOptions<T> // Optional request options
    ): Promise<ApiResponse<T>> => {
      // Setting up the fetch options

      const fetchOptions: RequestInit = {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${accessToken}`
        }
      };

      // If a body is provided, add it to the fetch options
      if (options?.body) {
        fetchOptions.body = JSON.stringify(options.body);
      }

      // Create a promise for the fetch response
      const responsePromise = new Promise<{ data: T; status: number }>(async (resolve, reject) => {
        try {
          const url = options?.test ? `${process.env.REACT_APP_API}/api/${route}` : `${process.env.REACT_APP_API}/v1.0/${route}`;
          // Fetching data from the API - will be replaced in the future with the api url via env variables
          const res = await fetch(url, fetchOptions);

          //console.log(res);

          // If the response is not ok, reject the promise with an error
          if (!res.ok || options?.mockError || options?.mockResponse) {
            if (options?.mockResponse) {
              resolve({ data: options.mockResponse, status: res.status });
              return;
            }
            const error = options?.mockError ? options.mockError : await getErrorMessage(res);
            if (setError) {
              setError(error);
            }

            reject({
              error: new Error('Request failed'),
              message: error,
              status: res.status
            });
            return;
          }

          // Try to parse the response as JSON
          let responseData: T;
          try {
            responseData = await res.json();
          } catch {
            // If parsing fails, reject the promise with an error
            const x = 'success' as unknown as T;
            resolve({ data: x, status: res.status });
            return;
          }

          // Resolve the promise with the parsed data
          resolve({
            data: responseData,
            status: res.status
          });
        } catch (error) {
          // Log the error and reject the promise
          reject({ error, status: 500 });
        }
      });

      // If toast options are provided, display a toast notification
      if (options?.toast) {
        // toast.promise(responsePromise, {
        //   loading: "Loading...", // Message while loading
        //   success: options.toast.toastText || "Success!", // Success message
        //   error: (err) => (err instanceof Error ? err.message : "An error occurred"), // Error message
        // });
        toast.promise(responsePromise, {
          loading: 'Loading...', // Message while loading
          success: options.toast.toastText || 'Success!', // Success message
          error: (err) => (err instanceof Error ? err.message : 'Es ist ein Fehler aufgetreten') // Error message
        });
      }

      // Wait for the response and handle the result
      try {
        const data = await responsePromise;
        return { data: data.data, error: undefined, status: data.status }; // Return the data if successful
      } catch (errorCatched) {
        const error = errorCatched as unknown as {
          message: string;
          error: Error | unknown;
          status: number;
        };
        return { data: undefined, error: error.message, status: error.status }; // Return the error if failed
      }
    },
    [accessToken, setError]
  );

  const apiFileRequest = useCallback(
    async <T>(
      route: string,
      method: 'POST' | 'DELETE' | 'GET' | 'PUT' | 'PATCH',
      file?: any,
      options?: ApiFileRequestOptions
    ): Promise<ApiResponse<T>> => {
      const myHeaders = new Headers();
      myHeaders.append('Authorization', `Bearer ${accessToken}`);

      const formdata = new FormData();
      if (file) {
        if (options?.formdataName) {
          if (Array.isArray(file)) {
            file.forEach((f, i) => {
              formdata.append(options.formdataName, f, f.name);
            });
          } else {
            formdata.append(options.formdataName, file, file.name);
          }
        } else {
          formdata.append(Array.isArray(file) ? 'files' : 'file', Array.isArray(file) ? file[0] : file, file.name);
        }
      }
      if (options?.customFields) {
        Object.keys(options.customFields).forEach((key) => {
          formdata.append(key, options.customFields ? options.customFields[key] : '');
        });
      }

      const requestOptions: RequestInit = {
        method: method,
        headers: myHeaders,
        body: formdata
      };

      const responsePromise = new Promise<{ data: T; status: number }>(async (resolve, reject) => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API}/v1.0/${route}`, requestOptions);

          if (!response.ok) {
            const error = await getErrorMessage(response);
            if (setError) {
              setError(error);
            }
            reject({
              error: new Error('Request failed'),
              message: error,
              status: response.status
            });
            return;
          }

          let responseData: T;
          try {
            responseData = await response.json();
          } catch {
            reject({
              error: new Error('Failed to parse JSON'),
              message: 'Failed to parse JSON',
              status: response.status
            });
            return;
          }

          resolve({
            data: responseData,
            status: response.status
          });
        } catch (error) {
          reject({ error, status: 500 });
        }
      });

      if (options?.toast) {
        toast.promise(responsePromise, {
          loading: 'Loading...',
          success: options.toast.toastText || 'Success!',
          error: (err) => (err instanceof Error ? err.message : 'Es ist ein Fehler aufgetreten')
        });
      }

      try {
        const data = await responsePromise;
        return { data: data.data, error: undefined, status: data.status };
      } catch (errorCatched) {
        const error = errorCatched as unknown as {
          message: string;
          error: Error | unknown;
          status: number;
        };
        if (error.status > 200 && error.status < 299) {
          toast.success('File erfolgreich hochgeladen');
          return { data: undefined, error: undefined, status: error.status };
        }
        toast.error('Es gab leider ein Problem. Bitte versuche es später wieder oder kontaktiere den Support');
        return { data: undefined, error: error.message, status: error.status };
      }
    },
    [accessToken, setError]
  );

  return { apiRequest, apiFileRequest }; // Return the apiRequest function
};

const getErrorMessage = async (res: Response) => {
  let errorMessage = '';
  let errorLocation = '';
  let error: ErrorI;
  let isClosable = true;
  let withReload = false;
  let title = 'Es ist ein Fehler aufgetreten';
  let redirect = false;

  try {
    error = (await res.json()) as ErrorI;
  } catch {
    throw new Error('Failed to parse JSON');
  }

  if (!error) {
    throw new Error('Failed to parse JSON');
  }

  if (res.status === 400) {
    errorMessage = '400 - Die Angaben scheinen nicht ganz korrekt zu sein. Bitte korrigiere das und versuche es nochmals.';
  } else if (res.status === 409) {
    errorMessage = '409 - Scheinbar gab es einen Konflikt. Bitte versuche es später nochmals oder kontaktiere den Support.';
  } else if (res.status === 403) {
    errorMessage = '403 - Du scheinst nicht die nötigen Rechte zu haben. Bitte kontaktiere den Support oder einen Organisationsadministrator.';
  } else if (res.status === 500) {
    errorMessage =
      '500 - Es ist ein Fehler aufgetreten, der nicht auftreten soll. Bitte schreibe dem Support. - Zeitpunkt: ' + new Date().toLocaleString();
  } else if (res.status === 404) {
    errorMessage = '404 - das Objekt wurde nicht gefunden. Bitte versuche es später nochmals oder kontaktiere den Support';
  } else if (res.status === 401) {
    errorMessage = '401 - Es ist ein unerwarteter Fehler aufgetreten. Bitte versuche dich erneut einzuloggen, oder kontaktiere den Support.';
    errorLocation = '/login';
  } else {
    errorMessage = `${res.status} - Es ist ein Fehler aufgetreten. Bitte versuche es später nochmals oder kontaktiere den Support`;
  }

  if (error.error === ErrorEntity.NoOrganizationForbidden) {
    errorLocation = '/register/informations';
    error.entity = ErrorEntity.NoOrganizationForbidden;
    redirect = true;
  } else if (error.error === ErrorEntity.IncompleteOnboardingUserInvitationsForbidden) {
    errorLocation = '/register/users';
    error.entity = ErrorEntity.IncompleteOnboardingUserInvitationsForbidden;
    redirect = true;
  } else if (error.error === ErrorEntity.IncompleteOnboardingIntroductionVideoForbidden) {
    errorLocation = '/register/video';
    error.entity = ErrorEntity.IncompleteOnboardingIntroductionVideoForbidden;
    redirect = true;
  } else if (error.error === ErrorEntity.EMailNotVerifiedForbidden) {
    errorLocation = '/verification/mail';
    error.entity = ErrorEntity.EMailNotVerifiedForbidden;
    redirect = true;
  } else if (error.error === ErrorEntity.Conflict) {
    error.entity = ErrorEntity.Conflict;
    if (error.message.includes('No organizations found - please add some')) {
      errorMessage = 'Es scheint, als hätten Sie noch keine Organisationen. Bitte fügen Sie eine hinzu.';
    } else if (error.message === 'Final URL is required for status DONE') {
      errorMessage = "Für den Status 'Erledigt' wird ein Link zu den finalen Dateien benötigt.";
    } else if (error.message === 'Files can only be uploaded to new jobs') {
      errorMessage = 'Dateien können nur bei nicht gestarteten Aufträgen hochgeladen werden.';
    } else if (error.message === 'Files can only be deleted from new jobs') {
      errorMessage = 'Dateien können nur bei nicht gestarteten Aufträgen gelöscht werden.';
    } else if (error.message === 'Organization already exists') {
      errorMessage = 'Dieses Unternehmen ist bereits registriert.';
    } else if (error.message === 'User already exists') {
      errorMessage = 'Der Benutzer existiert bereits.';
    } else if (error.message === 'User already has an invitation') {
      errorMessage = 'Der Benutzer hat bereits eine Einladung.';
    } else if (error.message === 'User or Organization Name already has an invitation') {
      errorMessage = 'Der Nutzer oder der Organisationsname wurde bereits eingeladen.';
    } else if (error.message === 'Organization is already cancelled') {
      errorMessage = 'Die Organisation wurde bereits gekündigt.';
    } else if (error.message === 'Job is already done') {
      errorMessage = 'Der Auftrag ist bereits abgeschlossen, deshalb ist diese Aktion nicht verfügbar';
    } else if (error.message === 'Rating already exists') {
      errorMessage = 'Ein Rating für diesen Auftrag existiert bereits.';
    } else if (error.message === 'User is archived') {
      errorMessage = 'Der Benutzer ist archiviert.';
    } else if (error.message === 'Team leader cannot be archived') {
      errorMessage = 'Der Teamleiter kann nicht archiviert werden.';
    } else if (error.message === 'Team leader cannot be changed as long as he has a team') {
      errorMessage = 'Der Teamleiter kann nicht geändert werden, solange er ein Team hat.';
    } else {
      errorMessage = 'Ein Konflikt ist aufgetreten. Bitte versuche es später nochmals oder kontaktiere den Support.';
    }
  } else if (error.error === ErrorEntity.MultipleAssignmentEnabledConflict) {
    error.entity = ErrorEntity.MultipleAssignmentEnabledConflict;
    errorMessage =
      'Für dieses Unternehmen ist die Zuweisung mehrerer Mitarbeiter aktiv. Bitte bearbeite die Zuweisung in der Zuweisungs-Karte oder deaktiviere sie zuerst.';
  } else if (error.error === ErrorEntity.MultipleAssignmentDisabledConflict) {
    error.entity = ErrorEntity.MultipleAssignmentDisabledConflict;
    errorMessage = 'Für dieses Unternehmen ist die Zuweisung mehrerer Mitarbeiter nicht aktiv. Bitte aktiviere sie zuerst über den Schalter.';
  } else if (error.error === ErrorEntity.MultipleAssignmentDisableNotUniqueConflict) {
    error.entity = ErrorEntity.MultipleAssignmentDisableNotUniqueConflict;
    errorMessage =
      'Die Zuweisung mehrerer Mitarbeiter lässt sich nur deaktivieren, wenn höchstens ein Mitarbeiter und eine Vertretung zugewiesen sind. Bitte entferne die übrigen zuerst.';
  } else if (error.error === ErrorEntity.AssignedCapacityMismatchBadRequest) {
    error.entity = ErrorEntity.AssignedCapacityMismatchBadRequest;
    errorMessage = 'Die verteilten Anteile ergeben nicht die erlaubte Jobsanzahl des Unternehmens. Bitte passe die Anteile an.';
  } else if (error.error === ErrorEntity.DuplicateAssignedInternalUserBadRequest) {
    error.entity = ErrorEntity.DuplicateAssignedInternalUserBadRequest;
    errorMessage = 'Ein Mitarbeiter kann diesem Unternehmen nur einmal zugewiesen werden.';
  } else if (error.error === ErrorEntity.FutureCapacityMultipleAssignmentConflict) {
    error.entity = ErrorEntity.FutureCapacityMultipleAssignmentConflict;
    errorMessage =
      'Eine zukünftige Kapazität kann nicht gesetzt werden, solange die Zuweisung mehrerer Mitarbeiter aktiv ist. Bitte deaktiviere sie zuerst.';
  } else if (error.error === ErrorEntity.JobFollowersMultipleAssignmentDisabledConflict) {
    error.entity = ErrorEntity.JobFollowersMultipleAssignmentDisabledConflict;
    errorMessage = 'Das Folgen ist nur bei Aufträgen von Unternehmen mit mehreren zugewiesenen Mitarbeitern möglich.';
  } else if (error.error === ErrorEntity.JobFollowerNotAssignedConflict) {
    error.entity = ErrorEntity.JobFollowerNotAssignedConflict;
    errorMessage = 'Nur Mitarbeiter, die diesem Unternehmen zugewiesen sind, können seinen Aufträgen folgen.';
  } else if (error.error === ErrorEntity.OrganizationLockedByOfferConflict) {
    error.entity = ErrorEntity.OrganizationLockedByOfferConflict;
    errorMessage =
      'Dieser Kunde wird bis zum Archivieren seines Angebots über das Angebot gepflegt. Bitte ändere die Daten dort unter „Vertragsdaten korrigieren“.';
  } else if (error.error === ErrorEntity.OfferMailAlreadyInUseConflict) {
    // Shown at the mail field of the offer form, not in the error modal.
    error.entity = ErrorEntity.OfferMailAlreadyInUseConflict;
    errorMessage = 'Diese E-Mail ist bereits registriert, bitte wende dich an einen Administrator!';
  } else if (error.error === ErrorEntity.OfferCompanyNameAlreadyInUseConflict) {
    // Shown at the company field of the offer form, not in the error modal.
    error.entity = ErrorEntity.OfferCompanyNameAlreadyInUseConflict;
    errorMessage = 'Die Firma ist bereits registriert, bitte wende dich an einen Administrator!';
  } else if (error.error === ErrorEntity.OfferProductNotAvailableBadRequest) {
    error.entity = ErrorEntity.OfferProductNotAvailableBadRequest;
    errorMessage =
      'Das Produkt ist inzwischen archiviert oder hat keinen Preis mehr für diese Laufzeit. Bitte lade die Seite neu und wähle es erneut.';
  } else if (error.error === ErrorEntity.OfferAddOnNotAvailableBadRequest) {
    error.entity = ErrorEntity.OfferAddOnNotAvailableBadRequest;
    errorMessage = 'Ein gewähltes Add-on ist inzwischen archiviert. Bitte lade die Seite neu und wähle die Add-ons erneut.';
  } else if (error.error === ErrorEntity.OfferStartDateInvalidBadRequest) {
    error.entity = ErrorEntity.OfferStartDateInvalidBadRequest;
    errorMessage = 'Das Startdatum ist nicht mehr wählbar. Bitte wähle ein anderes.';
  } else if (error.error === ErrorEntity.OfferNotOpenConflict) {
    error.entity = ErrorEntity.OfferNotOpenConflict;
    errorMessage = 'Das Angebot wurde inzwischen angenommen oder zurückgezogen. Bitte lade die Seite neu.';
  } else if (error.error === ErrorEntity.OfferNotDraftConflict) {
    error.entity = ErrorEntity.OfferNotDraftConflict;
    errorMessage = 'Das Angebot wurde inzwischen versendet und kann nicht mehr bearbeitet oder gelöscht werden. Bitte lade die Seite neu.';
  } else if (error.error === ErrorEntity.OfferNotSentConflict) {
    error.entity = ErrorEntity.OfferNotSentConflict;
    errorMessage = 'Ein Entwurf wird nicht zurückgezogen, sondern gelöscht. Bitte lade die Seite neu.';
  } else if (error.error === ErrorEntity.OfferArchivedConflict) {
    error.entity = ErrorEntity.OfferArchivedConflict;
    errorMessage = 'Das Angebot wurde inzwischen archiviert und kann nur noch angesehen werden. Bitte lade die Seite neu.';
  } else if (error.error === ErrorEntity.OfferNotClosedConflict) {
    error.entity = ErrorEntity.OfferNotClosedConflict;
    errorMessage = 'Nur angenommene, zurückgezogene oder abgelaufene Angebote können archiviert werden. Bitte lade die Seite neu.';
  } else if (error.error === ErrorEntity.OfferNotAcceptedConflict) {
    error.entity = ErrorEntity.OfferNotAcceptedConflict;
    errorMessage = 'Nur angenommene Angebote haben Vertragsdaten, die korrigiert werden können. Bitte lade die Seite neu.';
  } else if (error.error === ErrorEntity.OfferLinkInvalidNotFound) {
    // Shown as a page of the public checkout, not in the error modal.
    error.entity = ErrorEntity.OfferLinkInvalidNotFound;
    errorMessage = 'Dieser Link ist ungültig oder abgelaufen.';
  } else if (error.error === ErrorEntity.OfferAlreadyAcceptedConflict) {
    // Shown as a page of the public checkout, not in the error modal.
    error.entity = ErrorEntity.OfferAlreadyAcceptedConflict;
    errorMessage = 'Dieses Angebot wurde bereits angenommen.';
  } else if (error.error === ErrorEntity.OfferPaymentDetailsInvalidBadRequest) {
    error.entity = ErrorEntity.OfferPaymentDetailsInvalidBadRequest;
    errorMessage = 'Die Zahlungsangaben passen nicht zur Zahlungsart. Bitte prüfe IBAN, BIC und das SEPA-Lastschriftmandat und versuche es erneut.';
  } else if (error.error === ErrorEntity.OfferCheckoutTooManyRequests) {
    // Shown as a page or a toast of the public checkout, not in the error modal.
    error.entity = ErrorEntity.OfferCheckoutTooManyRequests;
    errorMessage = 'Zu viele Anfragen. Bitte versuche es in ein paar Minuten erneut.';
  } else if (error.error === ErrorEntity.NoUserFoundForbidden) {
    error.entity = ErrorEntity.NoUserFoundForbidden;
    errorMessage = 'Leider ist uns der angemeldete Nutzer nicht bekannt! Bitte kontaktiere einen Administrator, um Zugriff auf das Tool zu erhalten.';
    isClosable = false;
    errorLocation = '/login';
  } else if (error.message === 'One Filename already exists') {
    errorMessage = 'Eine Datei mit diesem Namen existiert bereits. Bitte benenne die Datei um und versuche es nochmals.';
  } else if (error.error === ErrorEntity.PaymentOverdueForbidden) {
    error.entity = ErrorEntity.PaymentOverdueForbidden;
    errorMessage =
      'Die Organisation wurde mit einer überfälligen Zahlung markiert. Bitte führe diese durch und / oder wende dich an einen Administrator.';
    isClosable = false;
  } else if (
    error.message ===
    'Diese Aktion ist leider nicht möglich, es gibt keinen anderen Auftrag mit dem Status neu in die Richtung in die du tauschen möchtest!'
  ) {
    errorMessage = 'Dieser Job kann nicht in diese Richtung verschoben werden.';
  } else if (error.message === 'Job is already done') {
    errorMessage = 'Diese Änderung erfordert, dass der Job Status nicht auf “Erledigt” steht.';
  } else if (error.message === 'Only not started jobs can be moved') {
    errorMessage = 'Nur Aufträge mit dem Status neu können getauscht werden!';
  } else if (error.message === 'One Filename already exists') {
    errorMessage = 'Einer der Dateinamen existiert bereits.';
  } else if (error.message === 'No job found to move') {
    errorMessage = 'Der Auftrag ist in diese Richtung nicht verschiebbar, da es keinen Auftrag gibt, mit dem die Position getauscht werden kann.';
  } else if (error.message === 'Job not found') {
    errorMessage = 'Der Auftrag wurde nicht gefunden.';
  } else if (error.message === 'Rating message not found') {
    errorMessage = 'Die Bewertungsnachricht wurde nicht gefunden.';
  } else if (error.error === ErrorEntity.NoUserFoundSelfCheckoutForbidden) {
    errorLocation = '/self-checkout/step1';
    error.entity = ErrorEntity.NoUserFoundSelfCheckoutForbidden;
    redirect = true;
  } else if (error.error === ErrorEntity.UserAwaitsApprovalForbidden) {
    errorLocation = '/self-checkout/step3';
    redirect = true;
  } else if (error.message.includes('iban must be an IBAN') || error.message.includes('bic must be a BIC or SWIFT code')) {
    if (error.message.includes('iban must be an IBAN') && error.message.includes('bic must be a BIC or SWIFT code')) {
      error.entity = ErrorEntity.InvalidIbanAndBicForbidden;
    } else {
      error.entity = error.message.includes('iban must be an IBAN') ? ErrorEntity.InvalidIbanForbidden : ErrorEntity.InvalidBicForbidden;
    }
  } else if (error.message.includes('Not enough free capacity slots available for this date')) {
    errorMessage = 'Es gibt für dieses Datum leider keine freien Kapazitäten mehr, bitte versuche einen anderen Zeitpunkt.';
  } else if (error.error === ErrorEntity.TotalFileSizeExceededBadRequest) {
    errorMessage =
      'Leider übersteigt der Upload die erlaubte Dateigröße. Bitte beachte das Limit von ' +
      (error.maxFileSize ? (error.maxFileSize / 1000000).toFixed(2) : 'FEHLER') +
      ' MB.';
  } else if (error.message === 'File size exceeds the 100mb of the order') {
    errorMessage = 'Die Maximalgröße an Dateien wurde überschritten. Pro Auftrag sind nur insgesamt 100MB erlaubt.';
  } else if (error.message.includes('Validation failed (expected size is less than')) {
    // TODO - remove
    const number = error.message.match(/\d+/);
    errorMessage = `Die Datei ist zu groß. Bitte wählen Sie eine kleinere Datei aus. (Maximalgröße: ${
      number ? parseInt(number as unknown as string) / 1000000 : '100'
    }MB)`;
  }

  const parsedError: ErrorI = {
    message: errorMessage,
    error: error.error,
    statusCode: res.status,
    entity: error.entity,
    location: {
      path: errorLocation,
      redirect
    },
    isClosable,
    withReload,
    title,
    mail: error.mail,
    name: error.name
  };

  return parsedError;
};

export default useApiRequest;
